import React, { useCallback, useMemo } from 'react';
import reduce from 'lodash/reduce';
import get from 'lodash/get';
import pt from 'prop-types';

import { FadeIn } from 'components/transitions';
import { getFormValues } from 'components/storefront/editor/utils/easyblocks';
import { evalConditions } from 'utils';

function withWidget(WidgetComponent) {
  const WrappedWidget = (props) => {
    const { value: propValue, field, onChange } = props;

    const onChangeHandler = useCallback(
      (_, value) => {
        const shouldCallOnChange = onChange && value !== propValue;

        if (shouldCallOnChange) {
          onChange(value);
        }
      },
      [propValue, onChange],
    );

    const isVisible = useEvalConditions(field);

    return (
      <FadeIn
        active={isVisible}
        transitionAppear={false}
        className="storefront-editor-widget"
      >
        <WidgetComponent {...props} onChange={onChangeHandler} />
      </FadeIn>
    );
  };

  WrappedWidget.propTypes = {
    value: pt.any,
    field: pt.object,
    params: pt.object,
    onChange: pt.func,
  };

  return WrappedWidget;
}

/**
 * @param {object} field
 * @returns {boolean}
 */
function useEvalConditions(field) {
  const fieldConditions = field.params?.conditions;
  const formValues = fieldConditions ? getFormValues() : null;

  const isVisible = useMemo(() => {
    if (!fieldConditions) {
      return true;
    }

    let contextValues = formValues;

    if (!contextValues) {
      return true;
    }

    const nameParts = field.name.split('.');
    if (nameParts.length > 1) {
      contextValues = get(formValues, nameParts.slice(0, nameParts.length - 1));
    }

    const flatValues = reduce(
      contextValues,
      (acc, value, key) => {
        if (typeof value === 'object' && value !== null) {
          acc[key] = value.value;
        }

        return acc;
      },
      {},
    );

    const match = evalConditions(fieldConditions, flatValues);

    return match;
  }, [formValues, fieldConditions, field.name]);

  return isVisible;
}

export default withWidget;
